import { useMediaQuery, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  GET_LIST,
  ListProps,
  Pagination,
  ReferenceField,
  sanitizeFetchType,
  TextField,
  TextInput,
  useQuery,
} from 'react-admin'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import List from '../../../common/customized-ra-components/List'
import FilteredReferenceInput from '../../../common/FilteredReferenceInput'
import FilterProps from '../../../common/FilterProps'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { CameraServerDto } from '../../../../core/dto/device/camera/camera-servers/camera-server.dto'
import {
  CheckCameraError,
  CheckLastRefreshError,
} from '../../common/modules/ModuleStatus'
import { CameraTypes } from '../../../../core/enum/CameraTypes'
import { CameraStatusDto } from '../../../../core/dto/device/camera/cameras/camera.dto-status'
import { cameraServerTypesAbleToBeVerified } from '../../../../core/constants/cameraServerTypesAbleToBeVerified'

const CameraFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="description" alwaysOn />
      {hasAuthority(Authority.VIEW_CAMERA_SERVERS) && (
        <FilteredReferenceInput
          source="serverId"
          reference={ResourceName.CAMERA_SERVERS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
    </Filter>
  )
}

export const CameraList = ({ ...props }: ListProps) => {
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const [cameraServersArray, setCameraServersArray] = useState<
    CameraServerDto[] | undefined
  >(undefined)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })
  const { data: cameraServers } = useQuery(
    {
      type: sanitizeFetchType(GET_LIST),
      resource: ResourceName.CAMERA_SERVERS,
      payload: { filter: {} },
    },
    {
      enabled:
        hasAuthority(Authority.VIEW_CAMERA_SERVERS) && !cameraServersArray,
    },
  )

  useEffect(() => {
    if (cameraServers) setCameraServersArray(cameraServers)
  }, [cameraServers])

  const postRowStyle = (record: any) => {
    const cameraError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )

    const cameraServerType = cameraServersArray?.find(
      (cameraServer) => cameraServer.id === record.serverId,
    )?.type as CameraTypes

    // Camera types not in that array are not colored at all
    if (!cameraServerTypesAbleToBeVerified.includes(cameraServerType)) {
      return {}
    }

    const cameraWarning = CheckCameraError(
      JSON.parse(record.jsonStatus) as CameraStatusDto,
      cameraServerType,
    )

    if (cameraError) {
      return {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      }
    }
    if (cameraWarning) {
      return {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
      }
    }
    return {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    }
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
      filters={<CameraFilters />}
      bulkActionButtons={false}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `/${ResourceName.CAMERAS}/${id}/show`}
      >
        <TextField source="id" label="ID" />
        <TextField source="description" />
        {!xSmallScreen && <TextField source="serverSideName" />}
        {hasAuthority(Authority.VIEW_CAMERA_SERVERS) && (
          <ReferenceField
            source="serverId"
            reference={ResourceName.CAMERA_SERVERS}
            link={false}
          >
            <FunctionField<CameraServerDto>
              source="name"
              render={(server?: CameraServerDto) =>
                `${server?.id}. ${server?.name}`
              }
            />
          </ReferenceField>
        )}
      </Datagrid>
    </List>
  )
}
